import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { RefreshCw, Sparkles, Quote, ArrowRight, Loader2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

/** Feelings used both here + Journal for continuity. */
const FEELINGS = [
  { key: "great", label: "Great", emoji: "😄", bg: "#E6FBF5", fg: "#06D6A0" },
  { key: "good", label: "Good", emoji: "🙂", bg: "#F0FDF4", fg: "#22C55E" },
  { key: "okay", label: "Okay", emoji: "😐", bg: "#FEF9C3", fg: "#B45309" },
  { key: "low", label: "Low", emoji: "😔", bg: "#EEF2FF", fg: "#4F46E5" },
  { key: "rough", label: "Rough", emoji: "😖", bg: "#FEE2E2", fg: "#DC2626" },
];

export default function MotivationCard() {
  const navigate = useNavigate();
  const [motiv, setMotiv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feeling, setFeeling] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/motivation/today");
        setMotiv(data.motivation);
        setFeeling(data.motivation?.feeling || null);
        setDismissed(!!data.motivation?.dismissed);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/motivation/refresh");
      setMotiv(data.motivation);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const chooseFeeling = async (f) => {
    setFeeling(f);
    setRefreshing(true);
    try {
      const { data } = await api.post("/motivation/feeling", { feeling: f });
      setMotiv(data.motivation);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const dismiss = async () => {
    setDismissed(true);
    try {
      await api.post("/motivation/dismiss");
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <div className="mb-8 rounded-[2rem] border border-[#FFE8E3] bg-white p-6 shadow-warm">
        <div className="flex items-center gap-2 text-[#718096]">
          <Loader2 className="h-4 w-4 animate-spin" /> <span>Preparing your daily dose…</span>
        </div>
      </div>
    );
  }
  if (dismissed || !motiv) return null;

  const content = motiv.content || {};
  const q = content.quote || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-8 overflow-hidden rounded-[2rem] border border-[#FFE8E3] bg-gradient-to-br from-white via-[#FFF9F5] to-[#FFF4E6] p-6 shadow-warm sm:p-8"
      data-testid="motivation-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sunrise text-white shadow-glow-coral">
            <Sparkles className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#C2410C]">
            Your daily coach
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="refresh-motivation"
            title="Refresh"
            className="rounded-full p-2 text-[#718096] hover:bg-white hover:text-[#FF7E67] disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={dismiss}
            data-testid="dismiss-motivation"
            title="Hide for today"
            className="rounded-full p-2 text-[#718096] hover:bg-white hover:text-[#FF7E67]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={motiv.updated_at || motiv.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <h2
            className="mt-3 font-nunito text-2xl font-extrabold leading-tight text-[#2D3748] sm:text-3xl"
            data-testid="motivation-greeting"
          >
            {content.greeting}
          </h2>
          {q.text && (
            <div className="mt-4 flex gap-3 rounded-2xl border border-[#FFE8E3] bg-white/80 p-4 backdrop-blur">
              <Quote className="h-5 w-5 shrink-0 text-[#FFB347]" strokeWidth={2.5} />
              <div>
                <p className="font-quicksand text-base italic text-[#2D3748]" data-testid="motivation-quote-text">
                  &ldquo;{q.text}&rdquo;
                </p>
                {q.author && (
                  <p className="mt-1 font-nunito text-xs font-bold text-[#C2410C]">— {q.author}</p>
                )}
              </div>
            </div>
          )}
          {content.story && (
            <p className="mt-4 font-quicksand text-sm text-[#4A5568]" data-testid="motivation-story">
              {content.story}
            </p>
          )}
          {content.encouragement && (
            <p className="mt-3 font-nunito text-sm font-bold text-[#C2410C]" data-testid="motivation-encouragement">
              {content.encouragement}
            </p>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-5 border-t border-[#FFE8E3] pt-4">
        <p className="mb-2 font-nunito text-sm font-bold text-[#2D3748]">How are you feeling?</p>
        <div className="flex flex-wrap gap-2" data-testid="feeling-picker">
          {FEELINGS.map((f) => {
            const isActive = feeling === f.key;
            return (
              <button
                key={f.key}
                onClick={() => chooseFeeling(f.key)}
                disabled={refreshing}
                data-testid={`feeling-${f.key}`}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 font-nunito text-xs font-bold transition-all ${
                  isActive
                    ? "scale-[1.05] border-[#FF7E67] shadow-warm"
                    : "border-[#FFE8E3] bg-white text-[#4A5568] hover:border-[#FF7E67]"
                }`}
                style={
                  isActive ? { backgroundColor: f.bg, color: f.fg } : undefined
                }
              >
                <span>{f.emoji}</span> {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {content.prompt && (
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl bg-white/70 p-3">
          <p className="flex-1 font-quicksand text-sm text-[#2D3748]" data-testid="motivation-prompt">
            {content.prompt}
          </p>
          <button
            onClick={() => navigate("/app/coach")}
            data-testid="motivation-help-btn"
            className="inline-flex items-center gap-1 rounded-full bg-sunrise px-4 py-2 font-nunito text-xs font-bold text-white shadow-glow-coral hover:scale-[1.03]"
          >
            Chat with coach <ArrowRight className="h-3 w-3" strokeWidth={3} />
          </button>
        </div>
      )}
    </motion.div>
  );
}
